import Loading from "components/center-loading";
import HeaderIcon from "components/header-icon";
import Pagination from "components/pagination";
import NotificationsIcon from "material-ui/svg-icons/social/notifications";
import moment from "moment";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { downloadFile } from "utils/custom-functions";
import Fetch from "utils/fetch";
import { NotificationsEmptyState, NotificationsTable, NotificationsToolbar } from "./components";
import { NotificationsContainer, NotificationsHeader, PaginationWrapper } from "./styled";
import type { ApiResponse, NotificationItem } from "./types";
import { PAGE_SIZE } from "./types";

const MasterdataAutomationNotifications: React.FC = () => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [processingStatuses, setProcessingStatuses] = useState<number[]>([]);
  const [minCreatedDate, setMinCreatedDate] = useState<string>("");
  const [maxCreatedDate, setMaxCreatedDate] = useState<string>("");

  const hasActiveFilters = useMemo<boolean>(() => {
    return Boolean(
      (processingStatuses && processingStatuses.length > 0) || minCreatedDate || maxCreatedDate
    );
  }, [processingStatuses, minCreatedDate, maxCreatedDate]);

  const buildQueryString = useCallback(
    (hasPagination: boolean = true, page: number = 0) => {
      const qs: string[] = [];
      if (hasPagination) {
        qs.push(`_page=${page}`);
        qs.push(`_size=${PAGE_SIZE}`);
      }

      if (minCreatedDate) qs.push(`_minCreatedDate=${encodeURIComponent(minCreatedDate)}`);
      if (maxCreatedDate)
      {
        const formattedMaxDate = moment(maxCreatedDate).endOf("day").format("YYYY-MM-DD HH:mm:ss");
        qs.push(`_maxCreatedDate=${encodeURIComponent(formattedMaxDate)}`);
      }

      if (processingStatuses?.length) {
        for (const s of processingStatuses) {
          qs.push(`ProcessingStatus=${encodeURIComponent(s)}`);
        }
      }

      return qs.join("&");
    },
    [minCreatedDate, maxCreatedDate, processingStatuses]
  );

  const searchNotificationsAsync = useCallback(
    async (page: number = 0) => {
      setIsFetching(true);

      try {
        const qs = buildQueryString(true, page);
        const response: { data: ApiResponse } = await Fetch.get(
          `/masterdata-automation:paginated?${qs}`
        );

        setItems(response.data?.Data ?? []);
        setTotal(response.data?.TotalItems ?? 0);
        setCurrentPage(page);
      } finally {
        setIsFetching(false);
      }
    },
    [buildQueryString]
  );

  const handleDownloadFile = async () => {
    setIsFetching(true);
    try {
      const qs = buildQueryString(false);
      const response: { data: ApiResponse } = await Fetch.get(
        `/masterdata-automation:export?${qs}`
      );

      const fileData = moment(new Date()).format("YYYYMMDD_HHmmss");
      downloadFile(
        `${intl.get("masterdataAutomationNotifications.exportFile")}_${fileData}.csv`,
        "csv",
        response.data
      );
    } catch (error) {
      console.error("Error downloading file: ", error);
    } finally {
      setIsFetching(false);
    }
  };

  const applyFiltersAsync = useCallback(async () => {
    await searchNotificationsAsync(0);
  }, [searchNotificationsAsync]);

const clearFilters = useCallback(async () => {
  setProcessingStatuses([]);
  setMinCreatedDate("");
  setMaxCreatedDate("");

  setIsFetching(true);
  try {
    const qs = `_page=0&_size=${PAGE_SIZE}`;
    const response: { data: ApiResponse } = await Fetch.get(
      `/masterdata-automation:paginated?${qs}`
    );

    setItems(response.data?.Data ?? []);
    setTotal(response.data?.TotalItems ?? 0);
    setCurrentPage(0);
  } finally {
    setIsFetching(false);
  }
}, []);



  const handleStatusesChange = useCallback((value: number[]) => {
    setProcessingStatuses(value);
  }, []);

  const handleDateChange = useCallback((prop: string, value: string) => {
    if (prop === "dataDe") {
      setMinCreatedDate(value);
    } else {
      setMaxCreatedDate(value);
    }
  }, []);

  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    searchNotificationsAsync(0);
  }, [searchNotificationsAsync]);

  return (
    <NotificationsContainer>
      <Loading isLoading={isFetching} />

      <NotificationsHeader>
        <HeaderIcon
          title={intl.get("masterdataAutomationNotifications.title")}
          subtitle=""
          icon={
            <NotificationsIcon style={{ width: 45, height: 45 }} color="var(--color-brand-500)" />
          }
        />
      </NotificationsHeader>

      <NotificationsToolbar
        processingStatuses={processingStatuses}
        minCreatedDate={minCreatedDate}
        maxCreatedDate={maxCreatedDate}
        hasActiveFilters={hasActiveFilters}
        onStatusChange={handleStatusesChange}
        onDateChange={handleDateChange}
        onApplyFilters={applyFiltersAsync}
        onExportToCsv={handleDownloadFile}
        onClearFilters={clearFilters}
      />

      {items.length ? <NotificationsTable items={items} /> : <NotificationsEmptyState />}

      {items.length > 0 && (
        <PaginationWrapper>
          <Pagination
            page={currentPage}
            pageSize={PAGE_SIZE}
            amount={total}
            refreshSearch={searchNotificationsAsync}
          />
        </PaginationWrapper>
      )}
    </NotificationsContainer>
  );
};

export default MasterdataAutomationNotifications;
